import { Component } from '@builder.io/sdk';
import { linkRegexConfig } from '@/components/BuilderIO/utils/commonFields';
import { TitleFields, MarginField, AnchorField, ButtonFields } from './CommonFields';

const buyingGuideComponents: { tabName: string; path: string; components: Record<string, Component> } = {
  tabName: 'Buying Guide Components',
  path: 'buyingGuideComponents',
  components: {
    BuyingGuideBanner: {
      name: 'Buying Guide Banner',
      image:
        'https://cdn.builder.io/api/v1/image/assets%2F9f2a69003c86470ea05deb9ecb9887be%2F64dc7026f1c94142b45dccbe5e1afe17',
      inputs: [
        {
          name: 'optionalHeader',
          type: 'object',
          subFields: [...TitleFields],
          required: false,
        },
        {
          name: 'image',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'gif'],
          required: true,
        },
        {
          name: 'responsiveImage',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'gif'],
          required: false,
        },
        {
          name: 'altImage',
          type: 'string',
          required: true,
        },
        {
          name: 'overlay',
          type: 'file',
          allowedFileTypes: ['svg'],
          required: false,
        },
        {
          name: 'videoUri',
          type: 'url',
          regex: { ...linkRegexConfig },
          required: false,
        },
        {
          name: 'videoPoster',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
          required: false,
        },
        {
          name: 'title',
          type: 'object',
          subFields: [...TitleFields],
        },
        {
          name: 'body',
          type: 'richText',
          required: true,
        },
        {
          name: 'contentPosition',
          type: 'string',
          defaultValue: 'left',
          enum: ['left', 'right'],
          required: false,
        },
        {
          name: 'textColor',
          type: 'string',
          defaultValue: 'black',
          enum: ['black', 'white'],
          required: false,
        },
        {
          name: 'optionalButtons',
          type: 'list',
          subFields: [
            {
              name: 'text',
              type: 'text',
            },
            {
              name: 'url',
              type: 'url',
            },
            {
              name: 'newTab',
              type: 'boolean',
              defaultValue: true,
            },
          ],
        },
        {
          name: 'buttonBorder',
          type: 'string',
          defaultValue: 'none',
          enum: ['none', 'black', 'white'],
          required: false,
        },
        {
          name: 'buttonHover',
          type: 'string',
          defaultValue: 'black',
          enum: ['black', 'white'],
          required: false,
        },
        {
          name: 'buttonBackgroundColor',
          type: 'string',
          defaultValue: 'white',
          enum: ['white', 'transparent'],
          required: false,
        },
        MarginField,
        AnchorField,
      ],
    },
    CopyBlock: {
      name: 'CopyBlock',
      image:
        'https://cdn.builder.io/api/v1/image/assets%2F9f2a69003c86470ea05deb9ecb9887be%2F78228096521e403f896fadb423c5db43',
      inputs: [
        {
          name: 'optionalLargeHeader',
          type: 'object',
          subFields: [...TitleFields],
        },
        MarginField,
        AnchorField,
        {
          name: 'mainImage',
          helperText:
            'You can select an image from your media library. (If you select a video it will prioritize the video).',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
        },
        {
          name: 'mainVideo',
          helperText:
            'You can select a video from your media library. (If you have selected an image it will prioritize the video).',
          type: 'file',
          allowedFileTypes: ['mp4', 'mov'],
        },
        {
          name: 'videoURI',
          helperText:
            'You can enter the URI link of a video from any source. (If you have selected an image or a video it will prioritize the link).',
          type: 'url',
        },
        {
          name: 'title',
          helperText: 'Video title description for screen readers',
          type: 'string',
          showIf: "options.get('videoURI')",
        },
        {
          name: 'imagePosition',
          type: 'string',
          defaultValue: 'left',
          helperText: 'Choose the position of the image. If the position is "none" the image will not be displayed.',
          enum: ['left', 'right', 'none'],
        },
        {
          name: 'altImage',
          type: 'string',
        },
        {
          name: 'content',
          type: 'richText',
          required: true,
        },
        {
          name: 'hasRingSizerForm',
          type: 'boolean',
          defaultValue: false,
        },
        {
          name: 'actions',
          friendlyName: 'Actions',
          type: 'list',
          subFields: [
            {
              name: 'dropdown',
              type: 'boolean',
              defaultValue: false,
              friendlyName: 'Is this a dropdown button?',
              helperText:
                'Please keep the Href field empty if it is a dropdown button. A dropdown button is a button that on hover displays multiple actions to be chosen.',
            },
            {
              name: 'label',
              required: true,
              type: 'text',
            },
            {
              name: 'href',
              friendlyName: 'Link',
              type: 'url',
              regex: { ...linkRegexConfig },
              required: true,
              showIf: `!options.get('dropdown')`,
            },
            {
              name: 'type',
              type: 'string',
              defaultValue: 'secondary',
              enum: ['primary', 'secondary'],
              required: false,
            },
            {
              name: 'color',
              type: 'string',
              defaultValue: 'dark',
              enum: ['light', 'dark'],
              required: false,
            },
            {
              name: 'actions',
              type: 'list',
              showIf: `options.get('dropdown')`,
              subFields: [
                {
                  name: 'label',
                  type: 'text',
                },
                {
                  name: 'href',
                  type: 'url',
                  regex: { ...linkRegexConfig },
                },
                {
                  name: 'type',
                  type: 'string',
                  defaultValue: 'secondary',
                  enum: ['primary', 'secondary'],
                  required: false,
                },
                {
                  name: 'color',
                  type: 'string',
                  defaultValue: 'dark',
                  enum: ['light', 'dark'],
                  required: false,
                },
              ],
            },
          ],
        },
      ],
    },
    GeneralCarousel: {
      name: 'GeneralCarousel',
      image:
        'https://cdn.builder.io/api/v1/image/assets%2F9f2a69003c86470ea05deb9ecb9887be%2Fd430b0b2fa4b4635b2c7c8dd33b20fe4',
      inputs: [
        {
          name: 'optionalGlobalHeader',
          type: 'object',
          subFields: [...TitleFields],
        },
        {
          name: 'title',
          type: 'object',
          subFields: [...TitleFields],
        },
        {
          name: 'description',
          type: 'text',
        },
        {
          name: 'shopUrl',
          type: 'text',
          defaultValue: '/engagement-ring-settings/',
          regex: { ...linkRegexConfig },
        },
        {
          name: 'infinite',
          type: 'boolean',
          defaultValue: false,
        },
        MarginField,
        AnchorField,
        {
          name: 'items',
          type: 'list',
          subFields: [
            {
              name: 'img',
              type: 'file',
              allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
              required: true,
            },
            {
              name: 'title',
              type: 'text',
              required: true,
            },
            {
              name: 'description',
              type: 'text',
            },
            {
              name: 'url',
              type: 'text',
              regex: { ...linkRegexConfig },
              required: true,
            },
            {
              name: 'alt',
              type: 'text',
              defaultValue: 'slide',
            },
          ],
        },
        {
          name: 'optionalButtons',
          type: 'list',
          subFields: [
            {
              name: 'text',
              type: 'text',
            },
            {
              name: 'url',
              type: 'url',
              regex: { ...linkRegexConfig },
            },
            {
              name: 'newTab',
              type: 'boolean',
              defaultValue: false,
            },
          ],
        },
      ],
    },
    LinkSection: {
      name: 'LinkSection',
      image:
        'https://cdn.builder.io/api/v1/image/assets%2F9f2a69003c86470ea05deb9ecb9887be%2Fc349b7f513c74e868bae0894d58475c8',
      inputs: [
        {
          name: 'title',
          type: 'object',
          subFields: [...TitleFields],
        },
        MarginField,
        AnchorField,
        {
          name: 'links',
          type: 'list',
          required: true,
          min: 2,
          subFields: [
            {
              name: 'label',
              type: 'text',
              required: true,
            },
            {
              name: 'url',
              type: 'link',
              regex: { ...linkRegexConfig },
              required: true,
            },
          ],
        },
      ],
    },
    ThreeAcross: {
      name: 'ThreeAcross',
      inputs: [
        {
          name: 'title',
          type: 'object',
          subFields: [...TitleFields],
        },
        {
          name: 'alignment',
          type: 'text',
          enum: ['left', 'center', 'right'],
          helperText: 'Defaults to left',
        },
        MarginField,
        AnchorField,
        {
          name: 'items',
          type: 'list',
          required: true,
          subFields: [
            {
              name: 'title',
              type: 'object',
              required: true,
              subFields: [...TitleFields],
            },
            {
              name: 'content',
              type: 'text',
              required: true,
            },
            {
              name: 'image',
              type: 'file',
              allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
              required: true,
            },
            {
              name: 'alt',
              type: 'text',
              required: true,
            },
            {
              name: 'link',
              type: 'url',
              regex: { ...linkRegexConfig },
            },
            {
              name: 'optionalButton',
              type: 'object',
              subFields: [...ButtonFields],
            },
          ],
        },
      ],
    },
    BirthstoneCarousel: {
      name: 'BirthstoneCarousel',
      image:
        'https://cdn.builder.io/api/v1/image/assets%2F9f2a69003c86470ea05deb9ecb9887be%2Fd430b0b2fa4b4635b2c7c8dd33b20fe4',
      inputs: [
        {
          name: 'optionalHeader',
          friendlyName: 'Item Title',
          type: 'text',
        },
        {
          name: 'items',
          type: 'list',
          subFields: [
            {
              name: 'month',
              type: 'text',
              required: true,
            },
            {
              name: 'name',
              type: 'text',
              required: true,
            },
            {
              name: 'anchor',
              type: 'text',
              required: true,
            },
            {
              name: 'img',
              type: 'file',
              allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
              required: true,
            },
            {
              name: 'altText',
              type: 'text',
              required: false,
            },
          ],
        },
        MarginField,
        AnchorField,
      ],
    },
    CaratSizingImage: {
      name: 'Carat Sizing Image',
      inputs: [
        {
          name: 'title',
          type: 'string',
          required: false,
          defaultValue: 'Approximate Carat Weight Size',
        },
        {
          name: 'description',
          type: 'string',
          required: false,
          defaultValue:
            ' This diagram illustrates the typical size relationship between diamonds of increasing carat weights. While the measurements below are typical, every diamond is unique. ',
        },
      ],
    },
    CaratSliderHandGraphic: {
      name: 'Diamond Carat',
      inputs: [
        {
          name: 'optionalLargeHeader',
          type: 'object',
          subFields: [...TitleFields],
          required: true,
        },
        {
          name: 'content',
          type: 'richText',
          required: true,
        },
        {
          name: 'contentPosition',
          type: 'string',
          defaultValue: 'right',
          helperText: 'Choose the position of the image. Defaults to left',
          enum: ['left', 'right'],
        },
        {
          name: 'mediaAlignment',
          type: 'string',
          defaultValue: 'start',
          helperText: 'Choose the vertical alignment of the image. Defaults to start',
          enum: ['start', 'center'],
        },
        {
          name: 'actions',
          friendlyName: 'Actions',
          type: 'list',
          subFields: [
            {
              name: 'dropdown',
              type: 'boolean',
              defaultValue: false,
              friendlyName: 'Is this a dropdown button?',
              helperText:
                'Please keep the Href field empty if it is a dropdown button. A dropdown button is a button that on hover displays multiple actions to be chosen.',
            },
            {
              name: 'label',
              required: true,
              type: 'text',
            },
            {
              name: 'href',
              friendlyName: 'Link',
              type: 'url',
              regex: { ...linkRegexConfig },
              required: true,
              showIf: `!options.get('dropdown')`,
            },
            {
              name: 'type',
              type: 'string',
              defaultValue: 'secondary',
              enum: ['primary', 'secondary'],
              required: false,
            },
            {
              name: 'color',
              type: 'string',
              defaultValue: 'dark',
              enum: ['light', 'dark'],
              required: false,
            },
            {
              name: 'actions',
              type: 'list',
              showIf: `options.get('dropdown')`,
              subFields: [
                {
                  name: 'label',
                  type: 'text',
                },
                {
                  name: 'href',
                  type: 'url',
                  regex: { ...linkRegexConfig },
                },
                {
                  name: 'type',
                  type: 'string',
                  defaultValue: 'secondary',
                  enum: ['primary', 'secondary'],
                  required: false,
                },
                {
                  name: 'color',
                  type: 'string',
                  defaultValue: 'dark',
                  enum: ['light', 'dark'],
                  required: false,
                },
              ],
            },
          ],
        },
        MarginField,
        AnchorField,
      ],
    },
    DiamondClarityChart: {
      name: 'DiamondClarityChart',
      inputs: [
        {
          name: 'title',
          type: 'object',
          subFields: [...TitleFields],
          required: true,
        },
        {
          name: 'tableOnLeft',
          type: 'boolean',
        },
        {
          name: 'content',
          type: 'richText',
          required: true,
        },
        MarginField,
        AnchorField,
      ],
    },
    DiamondClarityDiagram: {
      name: 'DiamondClarityDiagram',
      inputs: [
        {
          name: 'optionalLargeHeader',
          type: 'object',
          subFields: [...TitleFields],
        },
        MarginField,
        AnchorField,
        {
          name: 'contentPosition',
          type: 'string',
          defaultValue: 'left',
          helperText: 'Choose the position of the image. If the position is "none" the image will not be displayed.',
          enum: ['left', 'right', 'none'],
        },
        {
          name: 'mediaAlignment',
          type: 'string',
          defaultValue: 'start',
          helperText: 'Choose the vertical alignment of the image.',
          enum: ['start', 'center'],
        },
        {
          name: 'content',
          type: 'richText',
          required: true,
        },
        {
          name: 'actions',
          friendlyName: 'Actions',
          type: 'list',
          subFields: [
            {
              name: 'dropdown',
              type: 'boolean',
              defaultValue: false,
              friendlyName: 'Is this a dropdown button?',
              helperText:
                'Please keep the Href field empty if it is a dropdown button. A dropdown button is a button that on hover displays multiple actions to be chosen.',
            },
            {
              name: 'label',
              required: true,
              type: 'text',
            },
            {
              name: 'href',
              friendlyName: 'Link',
              type: 'url',
              regex: { ...linkRegexConfig },
              required: true,
              showIf: `!options.get('dropdown')`,
            },
            {
              name: 'type',
              type: 'string',
              defaultValue: 'secondary',
              enum: ['primary', 'secondary'],
              required: false,
            },
            {
              name: 'color',
              type: 'string',
              defaultValue: 'dark',
              enum: ['light', 'dark'],
              required: false,
            },
            {
              name: 'actions',
              type: 'list',
              showIf: `options.get('dropdown')`,
              subFields: [
                {
                  name: 'label',
                  type: 'text',
                },
                {
                  name: 'href',
                  type: 'url',
                  regex: { ...linkRegexConfig },
                },
                {
                  name: 'type',
                  type: 'string',
                  defaultValue: 'secondary',
                  enum: ['primary', 'secondary'],
                  required: false,
                },
                {
                  name: 'color',
                  type: 'string',
                  defaultValue: 'dark',
                  enum: ['light', 'dark'],
                  required: false,
                },
              ],
            },
          ],
        },
      ],
    },
    DiamondColorScaleChart: {
      name: 'DiamondColorScaleChart',
      inputs: [
        {
          name: 'title',
          type: 'object',
          subFields: [...TitleFields],
        },
        {
          name: 'content',
          type: 'richText',
          required: true,
        },
        {
          name: 'tableOnLeft',
          type: 'boolean',
          defaultValue: false,
          required: true,
        },
        MarginField,
        AnchorField,
      ],
    },
    DiamondColorScaleGraphic: {
      name: 'DiamondColorScaleGraphic',
      inputs: [
        {
          name: 'optionalLargeHeader',
          type: 'object',
          subFields: [...TitleFields],
        },
        {
          name: 'content',
          type: 'richText',
          required: true,
        },
        {
          name: 'graphicOnLeft',
          type: 'boolean',
          defaultValue: false,
          required: true,
        },
        {
          name: 'mediaAlignment',
          type: 'string',
          defaultValue: 'start',
          helperText: 'Choose the vertical alignment of the image.',
          enum: ['start', 'center'],
        },
        {
          name: 'actions',
          friendlyName: 'Actions',
          type: 'list',
          subFields: [
            {
              name: 'dropdown',
              type: 'boolean',
              defaultValue: false,
              friendlyName: 'Is this a dropdown button?',
              helperText:
                'Please keep the Href field empty if it is a dropdown button. A dropdown button is a button that on hover displays multiple actions to be chosen.',
            },
            {
              name: 'label',
              required: true,
              type: 'text',
            },
            {
              name: 'href',
              friendlyName: 'Link',
              type: 'url',
              regex: { ...linkRegexConfig },
              required: true,
              showIf: `!options.get('dropdown')`,
            },
            {
              name: 'type',
              type: 'string',
              defaultValue: 'secondary',
              enum: ['primary', 'secondary'],
              required: false,
            },
            {
              name: 'color',
              type: 'string',
              defaultValue: 'dark',
              enum: ['light', 'dark'],
              required: false,
            },
            {
              name: 'actions',
              type: 'list',
              showIf: `options.get('dropdown')`,
              subFields: [
                {
                  name: 'label',
                  type: 'text',
                },
                {
                  name: 'href',
                  type: 'url',
                  regex: { ...linkRegexConfig },
                },
                {
                  name: 'type',
                  type: 'string',
                  defaultValue: 'secondary',
                  enum: ['primary', 'secondary'],
                  required: false,
                },
                {
                  name: 'color',
                  type: 'string',
                  defaultValue: 'dark',
                  enum: ['light', 'dark'],
                  required: false,
                },
              ],
            },
          ],
        },
        MarginField,
        AnchorField,
      ],
    },
    DiamondCutsProgram: {
      name: 'Diamond Cuts Program',
      inputs: [
        {
          name: 'optionalLargeHeader',
          type: 'object',
          subFields: [...TitleFields],
          required: true,
        },
        {
          name: 'content',
          type: 'richText',
          required: true,
        },
        MarginField,
        AnchorField,
      ],
    },
    DiamondCharacteristicsDiagram: {
      name: 'Diamond Characteristics Diagram',
      inputs: [
        {
          name: 'optionalLargeHeader',
          type: 'object',
          subFields: [...TitleFields],
        },
        MarginField,
        AnchorField,
        {
          name: 'contentPosition',
          type: 'string',
          defaultValue: 'left',
          helperText: 'Choose the position of the image. If the position is "none" the image will not be displayed.',
          enum: ['left', 'right', 'none'],
        },
        {
          name: 'mediaAlignment',
          type: 'string',
          defaultValue: 'start',
          helperText: 'Choose the vertical alignment of the image.',
          enum: ['start', 'center'],
        },
        {
          name: 'content',
          type: 'richText',
          required: true,
        },
        {
          name: 'actions',
          friendlyName: 'Actions',
          type: 'list',
          subFields: [
            {
              name: 'dropdown',
              type: 'boolean',
              defaultValue: false,
              friendlyName: 'Is this a dropdown button?',
              helperText:
                'Please keep the Href field empty if it is a dropdown button. A dropdown button is a button that on hover displays multiple actions to be chosen.',
            },
            {
              name: 'label',
              required: true,
              type: 'text',
            },
            {
              name: 'href',
              friendlyName: 'Link',
              type: 'url',
              regex: { ...linkRegexConfig },
              required: true,
              showIf: `!options.get('dropdown')`,
            },
            {
              name: 'type',
              type: 'string',
              defaultValue: 'secondary',
              enum: ['primary', 'secondary'],
              required: false,
            },
            {
              name: 'color',
              type: 'string',
              defaultValue: 'dark',
              enum: ['light', 'dark'],
              required: false,
            },
            {
              name: 'actions',
              type: 'list',
              showIf: `options.get('dropdown')`,
              subFields: [
                {
                  name: 'label',
                  type: 'text',
                },
                {
                  name: 'href',
                  type: 'url',
                  regex: { ...linkRegexConfig },
                },
                {
                  name: 'type',
                  type: 'string',
                  defaultValue: 'secondary',
                  enum: ['primary', 'secondary'],
                  required: false,
                },
                {
                  name: 'color',
                  type: 'string',
                  defaultValue: 'dark',
                  enum: ['light', 'dark'],
                  required: false,
                },
              ],
            },
          ],
        },
      ],
    },
    DiamondCutInteractive: {
      name: 'Diamond Cut Interactive',
      inputs: [
        {
          name: 'optionalLargeHeader',
          type: 'object',
          subFields: [...TitleFields],
        },
        MarginField,
        AnchorField,
        {
          name: 'contentPosition',
          type: 'string',
          defaultValue: 'left',
          helperText: 'Choose the position of the image. If the position is "none" the image will not be displayed.',
          enum: ['left', 'right', 'none'],
        },
        {
          name: 'mediaAlignment',
          type: 'string',
          defaultValue: 'start',
          helperText: 'Choose the vertical alignment of the image.',
          enum: ['start', 'center'],
        },
        {
          name: 'content',
          type: 'richText',
          required: true,
        },
      ],
    },
    DiamondCutChart: {
      name: 'DiamondCutChart',
      inputs: [
        {
          name: 'optionalLargeHeader',
          type: 'object',
          subFields: [...TitleFields],
        },
        {
          name: 'optionalSubHeader',
          type: 'object',
          subFields: [
            ...TitleFields,
            {
              name: 'textAlignment',
              type: 'string',
              defaultValue: 'left',
              helperText: 'Text alignment. Defaults to left.',
              enum: ['left', 'right'],
            },
          ],
        },
        {
          name: 'desktopContent',
          type: 'richText',
          required: true,
        },
        {
          name: 'mobileContent',
          type: 'richText',
        },
        {
          name: 'contentPosition',
          type: 'string',
          defaultValue: 'right',
          helperText: 'Choose the position of the image. Defaults to right',
          enum: ['left', 'right'],
        },
        {
          name: 'mediaAlignment',
          type: 'string',
          defaultValue: 'start',
          helperText: 'Choose the vertical alignment of the image.',
          enum: ['start', 'center'],
        },
        MarginField,
        AnchorField,
      ],
    },
    MoissaniteDiamondTable: {
      name: 'MoissaniteDiamondTable',
      image:
        'https://cdn.builder.io/api/v1/image/assets%2F9f2a69003c86470ea05deb9ecb9887be%2Fc415de7acd63458abdcf6da15ed2f678',
      inputs: [
        {
          name: 'moissaniteVideo',
          type: 'file',
          allowedFileTypes: ['mp4'],
          required: true,
        },
        {
          name: 'diamondVideo',
          type: 'file',
          allowedFileTypes: ['mp4'],
          required: true,
        },
        {
          name: 'labDiamondVideo',
          type: 'file',
          allowedFileTypes: ['mp4'],
          required: true,
        },
        MarginField,
        AnchorField,
      ],
    },
    SapphireChoosing: {
      name: 'Choose Your Sapphire',
      inputs: [
        {
          name: 'optionalLargeHeader',
          type: 'object',
          subFields: [...TitleFields],
        },
        MarginField,
        AnchorField,
        {
          name: 'contentPosition',
          type: 'string',
          defaultValue: 'top',
          helperText: 'Choose the position of the image. If the position is "none" the image will not be displayed.',
          enum: ['top', 'bottom'],
        },
        {
          name: 'content',
          type: 'richText',
          required: true,
        },
      ],
    },
    RingSizeTable: {
      name: 'RingSizeTable',
      image:
        'https://cdn.builder.io/api/v1/image/assets%2F9f2a69003c86470ea05deb9ecb9887be%2Fc415de7acd63458abdcf6da15ed2f678',
      inputs: [MarginField, AnchorField],
    },
  },
};

export default buyingGuideComponents;
